// @ts-nocheck
/**
 * User Hierarchy dialog — shows the parent-chain / children tree for a
 * user. Ported to shadcn Dialog to drop the react-bootstrap dependency
 * and match obd3-gui's visual language across the Users flow.
 *
 * Props contract preserved from the previous BSModal version:
 *   { show, setShow, user }
 *
 * The exported `HierarchyNode` component and `HIERARCHY_STYLES` string
 * stay named-exports so the company-hierarchy modal (which reuses them)
 * doesn't break.
 */
import { useEffect, useMemo, useState } from 'react';
import { getApiCall } from '../../../service/httpCall';
import { API_USER_HIERARCHY } from '../../../service/url';
import { ChevronDown, ChevronRight, Building2, Headphones, User, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/* Shared tree styles — also used by the company hierarchy modal. */
export const HIERARCHY_STYLES = `
  .uh-tree, .uh-children { list-style: none; margin: 0; padding: 0; }
  .uh-children { margin-left: 14px; border-left: 1px dashed hsl(var(--border)); padding-left: 6px; }
  .uh-row { display: flex; align-items: center; gap: 8px; padding: 6px 6px; border-radius: 6px; }
  .uh-toggle { background: none; border: none; padding: 0; display: inline-flex; align-items: center; cursor: pointer; }
  .uh-avatar { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: hsl(var(--primary) / 0.12); color: hsl(var(--primary)); flex: none; }
  .uh-username { font-weight: 600; }
  .uh-role { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 12px; background: hsl(var(--muted)); color: hsl(var(--foreground)); }
  .uh-company { display: inline-flex; align-items: center; font-size: 12px; background: hsl(var(--muted)); padding: 2px 8px; border-radius: 12px; color: hsl(var(--foreground)); }
  .uh-count { margin-left: auto; font-size: 11px; font-weight: 600; background: hsl(var(--muted)); border-radius: 10px; padding: 1px 8px; color: hsl(var(--foreground)); }
  .uh-row--target { background: hsl(var(--primary) / 0.12); box-shadow: inset 0 0 0 1px hsl(var(--primary) / 0.45); }
  .uh-viewing { font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; padding: 2px 8px; border-radius: 12px; background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); }
`;

export function HierarchyNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <li className="uh-node">
      <div
        className={node.is_target ? 'uh-row uh-row--target' : 'uh-row'}
        style={{ paddingLeft: `${depth * 4}px` }}
      >
        <button
          type="button"
          className="uh-toggle"
          onClick={() => hasChildren && setOpen((o) => !o)}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>

        <span className="uh-avatar" aria-hidden="true">
          <User size={14} strokeWidth={2.25} />
        </span>

        <span className="uh-username">{node.username || node.name || '—'}</span>

        {node.is_target ? <span className="uh-viewing">Viewing</span> : null}

        {node.role_label ? <span className="uh-role">{node.role_label}</span> : null}

        {node.company_name ? (
          <span className="uh-company">
            {node.is_callcenter ? (
              <Headphones size={12} style={{ marginRight: 3 }} />
            ) : (
              <Building2 size={12} style={{ marginRight: 3 }} />
            )}
            {node.company_name}
          </span>
        ) : null}

        {hasChildren ? <span className="uh-count">{node.children.length}</span> : null}
      </div>

      {hasChildren && open ? (
        <ul className="uh-children">
          {node.children.map((child) => (
            <HierarchyNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// Child → Parent view: one chain per leaf user, leaf at the top and each
// node's single "child" being its parent, so HierarchyNode renders it
// unchanged. Built from the same parent → child tree the API returns.
function invertTree(root) {
  const chains = [];
  const walk = (node, ancestors) => {
    const path = [...ancestors, node];
    if (node.children?.length) {
      node.children.forEach((child) => walk(child, path));
      return;
    }
    let inverted = null;
    path.forEach((n) => {
      inverted = { ...n, children: inverted ? [inverted] : [] };
    });
    chains.push(inverted);
  };
  if (root) walk(root, []);
  return chains;
}

const DIRECTIONS = [
  { key: 'ptc', label: 'Parent → Child' },
  { key: 'ctp', label: 'Child → Parent' },
];

/* Hierarchy tree with a Parent → Child / Child → Parent toggle. Shared by
 * the user and company hierarchy modals; `summary` sits left of the toggle. */
export function HierarchyView({ tree, summary = null }) {
  const [direction, setDirection] = useState('ptc');
  const invertedChains = useMemo(() => invertTree(tree), [tree]);

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        {summary || <span />}
        <div className="inline-flex gap-1 rounded-full border border-border/60 bg-muted/40 p-0.5">
          {DIRECTIONS.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDirection(d.key)}
              className={`rounded-full border-0 px-3 py-1 text-xs font-medium ${
                direction === d.key
                  ? 'bg-primary/15 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 font-semibold text-primary'
                  : 'bg-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <ul className="uh-tree">
        {direction === 'ptc' ? (
          <HierarchyNode node={tree} />
        ) : (
          invertedChains.map((chain, idx) => <HierarchyNode key={`${chain.id}-${idx}`} node={chain} />)
        )}
      </ul>
    </>
  );
}

function RenderUserHierarchyModal({ show, setShow, user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tree, setTree] = useState(null);
  const [total, setTotal] = useState(0);

  const onOpenChange = (next) => {
    setShow(next);
    if (!next) {
      setTree(null);
      setError('');
      setTotal(0);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (show && user?.id) {
      setLoading(true);
      setError('');
      setTree(null);
      getApiCall(API_USER_HIERARCHY.replace(':id', user.id))
        .then((data) => {
          if (cancelled) return;
          setTree(data?.tree || null);
          setTotal(data?.total || 0);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err?.response?.data?.message || 'Failed to load user hierarchy');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [show, user?.id]);

  return (
    <Dialog open={show} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            User Hierarchy
            {user?.username ? <span className="text-muted-foreground"> — {user.username}</span> : null}
          </DialogTitle>
          <DialogDescription>
            {loading
              ? 'Loading…'
              : tree
                ? `${total} user(s) in this hierarchy`
                : 'Parent-chain view.'}
          </DialogDescription>
        </DialogHeader>

        <style>{HIERARCHY_STYLES}</style>

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading hierarchy…
            </div>
          ) : error ? (
            <div className="py-6 text-center text-sm text-destructive">{error}</div>
          ) : tree ? (
            <HierarchyView tree={tree} />
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">No hierarchy found</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RenderUserHierarchyModal;
